import type { ReactNode } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { DailySalesDay } from '../../../../api/daily-sales';
import { SOURCES, chartRows, shortDate, type Measure, type SourceKey } from '../daily-sales-format';

import { DailySalesChartTooltip } from './DailySalesChartTooltip';
import { DailySalesLegend } from './DailySalesLegend';

export interface DailySalesChartProps {
  title: string;
  days: DailySalesDay[];
  today: string;
  sources: SourceKey[];
  measure: Measure;
  format: (value: number) => string;
  /** Shorter labels for the value axis, e.g. `$4K`. */
  axisFormat?: (value: number) => string;
  /** Beside the title: the window total, or a control. */
  aside?: ReactNode;
}

const AXIS_TICK = { fontSize: 11, fill: 'var(--nb-subtle)' };

export function DailySalesChart({
  title,
  days,
  today,
  sources,
  measure,
  format,
  axisFormat = format,
  aside,
}: DailySalesChartProps) {
  const series = SOURCES.filter((s) => sources.includes(s.key));
  const data = chartRows(days, sources, measure);

  return (
    <section className="nb-card" aria-label={title}>
      <div className="nb-card-hd">
        <h3 className="nb-display" style={{ fontSize: 13, letterSpacing: '.04em', margin: 0 }}>
          {title}
        </h3>
        {aside}
      </div>
      <DailySalesLegend sources={sources} />
      <div style={{ height: 260, marginTop: 10 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
            <CartesianGrid vertical={false} stroke="var(--nb-border)" />
            <XAxis
              dataKey="date"
              tickFormatter={shortDate}
              tick={AXIS_TICK}
              tickLine={false}
              axisLine={{ stroke: 'var(--nb-border)' }}
              minTickGap={12}
            />
            <YAxis
              tickFormatter={axisFormat}
              tick={AXIS_TICK}
              tickLine={false}
              axisLine={false}
              width={52}
            />
            <Tooltip
              cursor={{ fill: 'var(--nb-surface-2)' }}
              content={({ active, label, payload }) => (
                <DailySalesChartTooltip
                  active={active}
                  label={label === undefined ? undefined : String(label)}
                  rows={payload}
                  format={format}
                  today={today}
                />
              )}
            />
            {series.map((s, i) => (
              <Bar
                key={s.key}
                dataKey={s.key}
                name={s.name}
                stackId="sales"
                fill={s.color}
                maxBarSize={26}
                radius={i === series.length - 1 ? [3, 3, 0, 0] : 0}
                isAnimationActive={false}
              >
                {data.map((d) => (
                  <Cell key={d.date} fillOpacity={d.date === today ? 0.45 : 1} />
                ))}
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}
